import type { Candidate, MaskingTemplate } from '@core/types';

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function maskingFieldSet(template: MaskingTemplate | null): Set<string> {
  const masked = new Set<string>();
  if (template) {
    if (template.mask_full_name) masked.add('full_name');
    if (template.mask_email) masked.add('email');
    if (template.mask_phone) masked.add('phone');
    if (template.mask_linkedin_url) masked.add('linkedin_url');
    if (template.mask_current_company) masked.add('current_company');
    if (template.mask_location) masked.add('location');
  } else {
    ['full_name', 'email', 'phone', 'linkedin_url'].forEach((f) => masked.add(f));
  }
  return masked;
}

/** Single word / token: keep first & last letters with asterisks in the middle when long enough. */
export function partialMaskToken(word: string): string {
  const w = word.trim();
  if (!w) return w;
  if (w.length <= 1) return '*';
  if (w.length === 2) return w[0] + '*';
  if (w.length <= 4) return w[0] + '**' + w.slice(-1);
  return w.slice(0, 2) + '***' + w.slice(-1);
}

/** Full name: each name part partially masked (client sees shape, not full spelling). */
export function partialMaskPersonName(fullName: string): string {
  return fullName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(partialMaskToken)
    .join(' ');
}

export function partialMaskEmailAddr(email: string): string {
  const e = email.trim();
  const m = e.match(/^([^@]+)@([^@]+)$/);
  if (!m) return '***@***.***';
  const [, local, domain] = m;
  const dot = domain.lastIndexOf('.');
  const tld = dot >= 0 ? domain.slice(dot + 1) : '';
  const localVis = local.slice(0, Math.min(2, local.length));
  return `${localVis}***@***.${tld || '***'}`;
}

export function partialMaskPhoneDigits(phone: string): string {
  const raw = phone.trim();
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 4) return '*** ****';
  const last4 = digits.slice(-4);
  const plus = raw.startsWith('+');
  const ccLen = Math.min(2, Math.max(0, digits.length - 10));
  const prefix = plus && ccLen > 0 ? `+${digits.slice(0, ccLen)} ` : '';
  return `${prefix}*** *** ${last4}`;
}

export function partialMaskLinkedInDisplay(url: string): string {
  const u = url.trim();
  try {
    const href = u.startsWith('http') ? u : `https://${u}`;
    const parsed = new URL(href);
    if (/linkedin\.com/i.test(parsed.hostname)) {
      return 'linkedin.com/*** (profile)';
    }
  } catch {
    /* ignore */
  }
  return 'linkedin.com/*** (profile)';
}

export function partialMaskPhrase(text: string): string {
  return text
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(partialMaskToken)
    .join(' ');
}

/**
 * Client-safe resume body: partially mask PII per masking template (aligned with generate-masked-pdf).
 * Candidate's real name stays readable at a glance; emails/phones/URLs are heavily obscured.
 */
export function maskResumeBodyText(
  body: string,
  opts: {
    candidate: Pick<
      Candidate,
      'full_name' | 'email' | 'phone' | 'linkedin_url' | 'current_company' | 'location'
    >;
    template: MaskingTemplate | null;
  },
): string {
  const { candidate, template } = opts;
  const maskedFields = maskingFieldSet(template);
  let t = body;
  const fullName = candidate.full_name?.trim() ?? '';

  if (maskedFields.has('full_name') && fullName) {
    const masked = partialMaskPersonName(fullName);
    t = t.replace(new RegExp(escapeRegExp(fullName), 'gi'), masked);
  }
  if (maskedFields.has('email')) {
    const email = candidate.email?.trim();
    if (email) {
      t = t.replace(new RegExp(escapeRegExp(email), 'gi'), () => partialMaskEmailAddr(email));
    }
    t = t.replace(/\b[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b/g, (m) => partialMaskEmailAddr(m));
  }
  if (maskedFields.has('phone')) {
    const phone = candidate.phone?.trim();
    if (phone) {
      t = t.replace(new RegExp(escapeRegExp(phone), 'g'), () => partialMaskPhoneDigits(phone));
    }
    t = t.replace(/\+?\d[\d\s()./-]{8,22}\d/g, (m) => {
      const digits = m.replace(/\D/g, '');
      return digits.length >= 10 && digits.length <= 15 ? partialMaskPhoneDigits(m) : m;
    });
  }
  if (maskedFields.has('linkedin_url')) {
    const li = candidate.linkedin_url?.trim();
    if (li) {
      t = t.replace(new RegExp(escapeRegExp(li), 'gi'), partialMaskLinkedInDisplay(li));
    }
    t = t.replace(/\bhttps?:\/\/(?:www\.)?linkedin\.com\/[^\s)\]]+/gi, (m) => partialMaskLinkedInDisplay(m));
  }
  if (maskedFields.has('current_company')) {
    const company = candidate.current_company?.trim();
    if (company) {
      t = t.replace(new RegExp(escapeRegExp(company), 'gi'), partialMaskPhrase(company));
    }
  }
  if (maskedFields.has('location')) {
    const loc = candidate.location?.trim();
    if (loc) {
      t = t.replace(new RegExp(escapeRegExp(loc), 'gi'), partialMaskPhrase(loc));
    }
  }
  return t;
}
